import { ITema } from "../interfaces/ITema";

const DB_NAME = "spacefood-cache";
const STORE_NAME = "themes";
const DB_VERSION = 1;
const TTL = 24 * 60 * 60 * 1000; // 24 horas em milissegundos

interface CacheEntry {
    key: string;
    data: ITema | null; // null indica que o tema não existe na API
    timestamp: number;
    estabelecimentoUuid: string;
}

/**
 * Inicializa o banco de dados IndexedDB
 */
const initDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            reject(new Error("Erro ao abrir IndexedDB"));
        };

        request.onsuccess = () => {
            resolve(request.result);
        };

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            
            // Criar object store se não existir
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, { keyPath: "key" });
                objectStore.createIndex("estabelecimentoUuid", "estabelecimentoUuid", { unique: false });
                objectStore.createIndex("timestamp", "timestamp", { unique: false });
            }
        };
    });
};

/**
 * Verifica se o cache ainda é válido (não expirou)
 */
const isCacheValid = (timestamp: number): boolean => {
    const now = Date.now();
    return (now - timestamp) < TTL;
};

/**
 * Busca dados no cache
 * @param key - Chave única para identificar o cache (ex: "tema-light-{estabelecimentoUuid}")
 * @returns Dados do cache, null se sabemos que não existe, ou undefined se não há cache
 */
export const getCache = async (key: string): Promise<ITema | null | undefined> => {
    try {
        const db = await initDB();
        
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readonly");
            const objectStore = transaction.objectStore(STORE_NAME);
            const request = objectStore.get(key);

            request.onerror = () => {
                reject(new Error("Erro ao buscar cache"));
            };

            request.onsuccess = () => {
                const result: CacheEntry | undefined = request.result;
                
                if (!result) {
                    resolve(undefined); // Não há cache
                    return;
                }

                // Verificar se o cache expirou
                if (!isCacheValid(result.timestamp)) {
                    // Cache expirado, remover
                    clearCache(key);
                    resolve(undefined); // Cache expirado = não há cache
                    return;
                }

                resolve(result.data); // Retorna o tema ou null (se não existe)
            };
        });
    } catch (error) {
        console.error(`[IndexedDB] Erro ao buscar cache para key ${key}:`, error);
        return undefined;
    }
};

/**
 * Salva dados no cache
 * @param key - Chave única para identificar o cache
 * @param data - Dados do tema a serem salvos (null indica que o tema não existe)
 * @param estabelecimentoUuid - UUID do estabelecimento
 */
export const setCache = async (
    key: string,
    data: ITema | null,
    estabelecimentoUuid: string
): Promise<boolean> => {
    try {
        const db = await initDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const objectStore = transaction.objectStore(STORE_NAME);

            const cacheEntry: CacheEntry = {
                key,
                data,
                timestamp: Date.now(),
                estabelecimentoUuid,
            };

            const request = objectStore.put(cacheEntry);

            request.onerror = () => {
                reject(new Error("Erro ao salvar cache"));
            };

            request.onsuccess = () => {
                resolve(true);
            };
        });
    } catch (error) {
        console.error(`[IndexedDB] Erro ao salvar cache para key ${key}:`, error);
        return false;
    }
};

/**
 * Remove um item específico do cache
 * @param key - Chave do cache a ser removido
 */
export const clearCache = async (key: string): Promise<boolean> => {
    try {
        const db = await initDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const objectStore = transaction.objectStore(STORE_NAME);
            const request = objectStore.delete(key);

            request.onerror = () => {
                reject(new Error("Erro ao limpar cache"));
            };

            request.onsuccess = () => {
                resolve(true);
            };
        });
    } catch (error) {
        console.error("Erro ao limpar cache:", error);
        return false;
    }
};

/**
 * Limpa todo o cache de temas
 */
export const clearAllCache = async (): Promise<boolean> => {
    try {
        const db = await initDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const objectStore = transaction.objectStore(STORE_NAME);
            const request = objectStore.clear();

            request.onerror = () => {
                reject(new Error("Erro ao limpar todo o cache"));
            };

            request.onsuccess = () => {
                resolve(true);
            };
        });
    } catch (error) {
        console.error("[IndexedDB] Erro ao limpar todo o cache:", error);
        return false;
    }
};

/**
 * Limpa cache de um estabelecimento específico
 * @param estabelecimentoUuid - UUID do estabelecimento
 */
export const clearCacheByEstabelecimento = async (
    estabelecimentoUuid: string
): Promise<boolean> => {
    try {
        const db = await initDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], "readwrite");
            const objectStore = transaction.objectStore(STORE_NAME);
            const index = objectStore.index("estabelecimentoUuid");
            const request = index.openCursor(IDBKeyRange.only(estabelecimentoUuid));

            request.onerror = () => {
                reject(new Error("Erro ao limpar cache do estabelecimento"));
            };

            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve(true);
                }
            };
        });
    } catch (error) {
        console.error("Erro ao limpar cache do estabelecimento:", error);
        return false;
    }
};

